/**
 * Account & Settings types.
 * Used by Edit Profile, Settings, Notification Preferences, Onboarding.
 */

// ─── Profile ────────────────────────────────────────────────────────────────

export type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';
export type SexualOrientation = 'straight' | 'gay' | 'bi' | 'queer' | 'other';
export type LookingFor =
  | 'friendship'
  | 'companionship'
  | 'romantic_connection'
  | 'relationship'
  | 'shared_interest_groups';
export type InterestedIn = 'men' | 'women' | 'either';

export const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const ORIENTATION_OPTIONS: { value: SexualOrientation; label: string }[] = [
  { value: 'straight', label: 'Straight' },
  { value: 'gay', label: 'Gay' },
  { value: 'bi', label: 'Bi' },
  { value: 'queer', label: 'Queer' },
  { value: 'other', label: 'Other' },
];

export const LOOKING_FOR_OPTIONS: { value: LookingFor; label: string }[] = [
  { value: 'friendship', label: 'Friendship' },
  { value: 'companionship', label: 'Companionship' },
  { value: 'romantic_connection', label: 'Romantic Connection' },
  { value: 'relationship', label: 'Relationship' },
  { value: 'shared_interest_groups', label: 'Shared Interest Groups' },
];

export const INTERESTED_IN_OPTIONS: { value: InterestedIn; label: string }[] = [
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'either', label: 'Either' },
];

/** Editable user profile fields */
export interface EditableProfile {
  firstName: string;
  lastName: string;
  dateOfBirth: string;          // YYYY-MM-DD
  gender: Gender | '';
  sexualOrientation: SexualOrientation | '';
  showAge: boolean;
  showSexualOrientation: boolean;
  aboutMe: string;
  lookingFor: LookingFor | '';
  interestedIn: InterestedIn | '';
  /**
   * READ shape. Canonically a `string[]` of lowercased tags (PPL-490); the
   * comma-separated string older builds wrote is still on plenty of docs, so
   * every reader goes through `normalizeInterests` from utils/interests.
   * Writers only ever write the array.
   */
  interests: string[] | string;
  city: string;
  country: string;
  profileImageUrl: string;
  tetherHandle: string;
}

/** Profile image validation result from Cloud Function */
export interface ImageValidationResult {
  isValid: boolean;
  reason: string;
  timestamp: string;
}

/** Handle validation result */
export interface HandleValidationResult {
  isValid: boolean;
  reason?: string;
}

/** Handle availability result */
export interface HandleAvailabilityResult {
  isAvailable: boolean;
}

/** Handle update restriction */
export interface HandleRestrictionResult {
  canUpdate: boolean;
  nextAllowedDate?: string;
  lastHandleUpdate?: string;
}

// ─── Notification Settings ──────────────────────────────────────────────────

export type SmartBehaviour = 'f8' | 'inactive' | 'mute';
export type PushSetting = 'push' | 'off';
export type EmailFrequency = 'weekly' | 'monthly' | 'off';
export type EmailSetting = 'email' | 'off';

/**
 * The categories, keyed EXACTLY as the server's `DEFAULT_CATEGORY_SETTINGS`
 * keys them (`services/notificationSettingsService.js`). That list is the
 * authority: `pushAllowedForCategory` looks up the key the sender names, and
 * falls back to the server's own default when the user's document has no value
 * for it. A key web spells differently is a switch that decides nothing, and a
 * category web omits is a push the user cannot turn off (PPL-531).
 */
export interface CategorySettings {
  /**
   * PPL-531 — the server reads `aiNudges`; web used to write `intelligentPush`,
   * which a migration in `index.js` renames and DELETES. So the old key was a
   * toggle with no effect the moment that migration had run for an account.
   * Legacy values are carried over on load, see Settings.tsx.
   */
  aiNudges: PushSetting;
  generalCheckins: PushSetting;
  relationshipBoosters: PushSetting;
  birthdayReminders: PushSetting;
  eventReminders: PushSetting;
  momentReminders: PushSetting;
  /**
   * The posts feed. Spelled `tether_*` on the documents and the toggle for
   * historical reasons; the product word is "posts".
   */
  tetherNotifications: PushSetting;
  /**
   * PPL-431 — direct messages: the request someone sends you, and a message in
   * a conversation you accepted. `off` silences both the push and the email;
   * the email otherwise follows the master email switch, because web registers
   * no push token and would otherwise hear nothing at all.
   */
  directMessages: PushSetting;
  /** PPL-334 — the write-back review nudge ("f8 noticed something"). */
  insightsReview: PushSetting;
  /** PPL-277 — pre-meet briefs and the post-event "log it" nudge, one switch. */
  preMeetBriefs: PushSetting;
  /** PPL-16 — rooms: check-in nudges, pause/resume/end notices. */
  mediation: PushSetting;
  circleMessages: PushSetting;
  circleEvents: PushSetting;
  circleEventReminders: PushSetting;
  weeklySummary: EmailFrequency;
  productUpdates: EmailSetting;
}

export interface NotificationSettings {
  push: boolean;
  email: boolean;
  smartBehaviour: SmartBehaviour;
  smartDays: string;
  smartMuteEndDate?: any;        // Firestore Timestamp
  categorySettings: CategorySettings;
}

/**
 * Row order and wording for the settings list. Lowercase house voice, matching
 * the app's own screen label for label so the two surfaces name the same switch
 * the same way. "ppltlk" is gone: the forum is "posts", and that spelling was
 * never even the old product name.
 */
export const CATEGORY_LABELS: Record<keyof CategorySettings, string> = {
  aiNudges: 'intelligent suggestions',
  generalCheckins: 'emotional check-ins',
  relationshipBoosters: 'connection boosters',
  birthdayReminders: 'birthday reminders',
  eventReminders: 'event reminders',
  momentReminders: 'moment reminders',
  tetherNotifications: 'post notifications',
  directMessages: 'direct messages',
  insightsReview: 'life · review',
  preMeetBriefs: 'pre-meet briefs',
  mediation: 'rooms',
  circleMessages: 'circle messages',
  circleEvents: 'circle events',
  circleEventReminders: 'circle event reminders',
  weeklySummary: 'activity summary',
  productUpdates: 'product updates',
};

/** Categories that are email-only (not push) */
export const EMAIL_ONLY_CATEGORIES: (keyof CategorySettings)[] = [
  'weeklySummary',
  'productUpdates',
];

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  push: true,
  email: true,
  smartBehaviour: 'f8',
  smartDays: '',
  /**
   * Every value here must equal the server's `DEFAULT_CATEGORY_SETTINGS` for
   * the same key: a category the client defaults differently reads back as a
   * setting the user never chose (PPL-254, the absent≠off trap). `weeklySummary`
   * is the one deliberate difference — the server's "email" is not a frequency,
   * and monthly is the shipped default on both clients.
   */
  categorySettings: {
    aiNudges: 'push',
    generalCheckins: 'push',
    relationshipBoosters: 'push',
    birthdayReminders: 'push',
    eventReminders: 'push',
    momentReminders: 'push',
    tetherNotifications: 'push',
    directMessages: 'push',
    insightsReview: 'push',
    preMeetBriefs: 'push',
    mediation: 'push',
    circleMessages: 'push',
    circleEvents: 'push',
    circleEventReminders: 'push',
    weeklySummary: 'monthly',
    productUpdates: 'email',
  },
};

// ─── Onboarding ─────────────────────────────────────────────────────────────

export type RelationshipFocus =
  | 'family'
  | 'friends'
  | 'work/professional connections'
  | 'myself (self-improvement)'
  | 'romantic partner';

export type CommunicationStyle =
  | 'directly and openly'
  | 'with empathy and understanding'
  | 'clearly and concisely'
  | 'thoughtfully and reflectively';

export type RelationshipChallenge =
  | 'communication issues'
  | 'lack of time'
  | 'conflict resolution'
  | 'trust issues'
  | 'emotional connection';

export type CommunicationMethod =
  | 'texting'
  | 'phone calls'
  | 'video calls'
  | 'in-person'
  | 'social media';

export type SupportNeeded =
  | 'Improving Communication'
  | 'Resolving Conflicts'
  | 'Building Trust'
  | 'Making Time for Relationships'
  | 'Deepening Emotional Bonds';

export interface OnboardingData {
  firstName: string;
  relationshipFocus: RelationshipFocus | '';
  communicationStyle: CommunicationStyle | '';
  relationshipChallenge: RelationshipChallenge | '';
  communicationMethod: CommunicationMethod | '';
  supportNeeded: SupportNeeded | '';
}

export const RELATIONSHIP_FOCUS_OPTIONS: { value: RelationshipFocus; label: string; icon: string }[] = [
  { value: 'family', label: 'Family', icon: 'fas fa-house-chimney' },
  { value: 'friends', label: 'Friends', icon: 'fas fa-user-group' },
  { value: 'work/professional connections', label: 'Work / Professional', icon: 'fas fa-briefcase' },
  { value: 'myself (self-improvement)', label: 'Self-Improvement', icon: 'fas fa-seedling' },
  { value: 'romantic partner', label: 'Romantic Partner', icon: 'fas fa-heart' },
];

export const COMMUNICATION_STYLE_OPTIONS: { value: CommunicationStyle; label: string; icon: string }[] = [
  { value: 'directly and openly', label: 'Directly & Openly', icon: 'fas fa-bullhorn' },
  { value: 'with empathy and understanding', label: 'With Empathy', icon: 'fas fa-hand-holding-heart' },
  { value: 'clearly and concisely', label: 'Clearly & Concisely', icon: 'fas fa-list-check' },
  { value: 'thoughtfully and reflectively', label: 'Thoughtfully', icon: 'fas fa-brain' },
];

export const RELATIONSHIP_CHALLENGE_OPTIONS: { value: RelationshipChallenge; label: string; icon: string }[] = [
  { value: 'communication issues', label: 'communication issues', icon: 'fas fa-comment-dots' },
  { value: 'lack of time', label: 'lack of time', icon: 'fas fa-clock' },
  { value: 'conflict resolution', label: 'conflict resolution', icon: 'fas fa-hands-helping' },
  { value: 'trust issues', label: 'trust issues', icon: 'fas fa-hand-holding-heart' },
  { value: 'emotional connection', label: 'emotional connection', icon: 'fas fa-heart' },
];

export const COMMUNICATION_METHOD_OPTIONS: { value: CommunicationMethod; label: string; icon: string }[] = [
  { value: 'texting', label: 'texting', icon: 'fas fa-comment-dots' },
  { value: 'phone calls', label: 'phone calls', icon: 'fas fa-phone' },
  { value: 'video calls', label: 'video calls', icon: 'fas fa-comment-dots' },
  { value: 'in-person', label: 'in-person', icon: 'fas fa-user-group' },
  { value: 'social media', label: 'social media', icon: 'fas fa-newspaper' },
];

export const SUPPORT_NEEDED_OPTIONS: { value: SupportNeeded; label: string; icon: string }[] = [
  { value: 'Improving Communication', label: 'improving communication', icon: 'fas fa-bullhorn' },
  { value: 'Resolving Conflicts', label: 'resolving conflicts', icon: 'fas fa-hands-helping' },
  { value: 'Building Trust', label: 'building trust', icon: 'fas fa-hand-holding-heart' },
  { value: 'Making Time for Relationships', label: 'making time for relationships', icon: 'fas fa-clock' },
  { value: 'Deepening Emotional Bonds', label: 'deepening emotional bonds', icon: 'fas fa-heart' },
];
